import { createSystem } from '@iwsdk/core';
import * as THREE from 'three';
import { IAssistStrokeService, slugFromIdentity } from '@questvisionstream/client';
import { AppConfig } from '../config';
import { getServiceManager } from '../runtime';
import { poseTracking } from '../rendering/poseTracking';
import { StrokeStore, type LiveStroke } from '../rendering/strokeStore';
import { disposeTagObject } from '../rendering/TagFactory';

/** Stable palette slots for `color: "auto"` - a sender hash picks one, like the console. */
const AUTO_PALETTE = ['#ff6b6b', '#4cc3e0', '#ffd166', '#06d6a0', '#c77dff', '#ff9f43'];

function autoColor(sender: string): string {
  let hash = 0;
  for (const ch of slugFromIdentity(sender)) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return AUTO_PALETTE[hash % AUTO_PALETTE.length]!;
}

/**
 * Renders assistants' strokes in the wearer's world (Plan-04 §3.2) - the web
 * home of `StrokeRenderModule`. Each stroke becomes a `THREE.Line` whose
 * points are unprojected through the capture-time pose of the frame the
 * stroke was drawn on (`poseArrivalMs` from the stroke service, resolved by
 * the shared `poseTracking`), at the same placement distance as the detection
 * boxes - so a circle drawn around a box lands around the box.
 *
 * Appends reuse their stroke's frozen pose; committed strokes expire after
 * the TTL; the wearer's CLEAR (and an assistant's clear) drop them at once.
 */
export class StrokeRenderSystem extends createSystem({}) {
  private readonly store = new StrokeStore();
  private readonly lines = new Map<string, THREE.Line>();
  private unsub: (() => void) | undefined;
  private unsubReset: (() => void) | undefined;

  override init(): void {
    const strokes = getServiceManager().resolve(IAssistStrokeService);
    strokes.setRenderCounterSource(() => this.store.counters);
    this.unsub = strokes.on('strokeReceived', (arrival) => {
      const { changed, removed } = this.store.apply(arrival, performance.now());
      removed.forEach((id) => this.removeLine(id));
      changed.forEach((id) => this.rebuild(id));
    });
    this.unsubReset = () => strokes.setRenderCounterSource(null);
  }

  override update(): void {
    this.store.expire(performance.now()).forEach((id) => this.removeLine(id));
  }

  private rebuild(id: string): void {
    const stroke = this.store.live.find((s) => s.id === id);
    if (!stroke) return;
    this.removeLine(id);
    const line = this.build(stroke);
    if (!line) return;
    this.scene.add(line);
    this.lines.set(id, line);
    stroke.dirty = false;
  }

  private build(stroke: LiveStroke): THREE.Line | null {
    if (stroke.points.length < 2) return null;
    // No capture pose = nothing correct to place it against (geometry in the
    // wrong space is worse than none - the box modules' rule).
    if (stroke.poseArrivalMs === null) return null;
    const snapshot = poseTracking.snapshotForArrival(stroke.poseArrivalMs);
    if (!snapshot) return null;

    const world = stroke.points.map((p) =>
      poseTracking.unprojectAtArrival(p, stroke.poseArrivalMs!, AppConfig.placementDistanceMeters),
    );
    if (world.some((w) => w === null)) return null;

    const geometry = new THREE.BufferGeometry().setFromPoints(world as THREE.Vector3[]);
    const color = stroke.color === 'auto' ? autoColor(stroke.sender) : stroke.color;
    const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity: stroke.committed ? 0.95 : 0.7 });
    return new THREE.Line(geometry, material);
  }

  private removeLine(id: string): void {
    const line = this.lines.get(id);
    if (!line) return;
    this.scene.remove(line);
    disposeTagObject(line);
    this.lines.delete(id);
  }

  override destroy(): void {
    this.unsub?.();
    this.unsubReset?.();
    for (const id of [...this.lines.keys()]) this.removeLine(id);
    this.store.clear();
  }
}
